import React from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Container,
  Typography,
  IconButton,
  Stack,
  Button,
  Chip,
  Paper,
  LinearProgress,
  Tooltip,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import GridViewIcon from "@mui/icons-material/GridView";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";

import { AnimatePresence, motion } from "framer-motion";

const BRAND = "#125570";

const MotionBox = motion(Box);

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function useFullscreen() {
  const [isFs, setIsFs] = React.useState(false);

  React.useEffect(() => {
    const handler = () => setIsFs(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const toggle = async (el) => {
    try {
      if (!document.fullscreenElement) {
        await el.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch {
      // ignore
    }
  };

  return { isFs, toggle };
}

/** ---------- Slide Layout Shell ---------- */
function SlideShell({ title, subtitle, badge, children }) {
  return (
    <Paper
      sx={{
        borderRadius: 4,
        p: { xs: 3, md: 5 },
        minHeight: { xs: "72vh", md: "74vh" },
        border: "1px solid rgba(15, 23, 42, 0.10)",
        background:
          "linear-gradient(180deg, rgba(18, 85, 112, 0.12), rgba(255,255,255, 1))",
        boxShadow: "0 18px 46px rgba(2, 6, 23, 0.10)",
        overflow: "hidden",
      }}
    >
      <Stack spacing={2.2}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {badge ? (
            <Chip
              label={badge}
              sx={{
                bgcolor: "rgba(18, 85, 112, 0.12)",
                color: "primary.main",
                fontWeight: 900,
              }}
            />
          ) : null}
          <Chip
            label="@excelsiorservicosmetrologicos"
            sx={{ fontWeight: 800, opacity: 0.85 }}
          />
        </Stack>

        <Box>
          <Typography
            variant="h3"
            sx={{ fontWeight: 950, letterSpacing: -0.8, lineHeight: 1.1 }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography
              color="text.secondary"
              sx={{ mt: 1, fontSize: { xs: 16, md: 18 }, lineHeight: 1.8 }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ pt: 1 }}>{children}</Box>
      </Stack>
    </Paper>
  );
}

/** ---------- Slide 1 ---------- */
function Slide01() {
  return (
    <SlideShell
      badge="ABNT NBR ISO/IEC 17025:2017"
      title="Seja Bem-vindo!"
      subtitle="Escopo: Implementação do SGQ – ISO 17025 • Orientador: Felipe dos Reis"
    >
      <Stack spacing={2}>
        <Typography color="text.secondary" sx={{ lineHeight: 1.9, maxWidth: 920 }}>
          Nesta apresentação, você vai ver o panorama da metrologia, estrutura do Inmetro/Cgcre,
          rastreabilidade, divisão da norma ISO 17025 e práticas para estruturar um laboratório.
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
          <Chip label="Metrologia & Fundamentos" sx={{ fontWeight: 800 }} />
          <Chip label="ISO 17025 na prática" sx={{ fontWeight: 800 }} />
          <Chip label="Acreditação e preparação" sx={{ fontWeight: 800 }} />
        </Stack>
      </Stack>
    </SlideShell>
  );
}

/** ---------- Slide 2 ---------- */
function Slide02() {
  return (
    <SlideShell
      badge="Sobre o Autor"
      title="Felipe dos Reis"
      subtitle="Formação e experiência profissional"
    >
      <Stack spacing={2}>
        <Paper
          sx={{
            p: 2.5,
            borderRadius: 3,
            bgcolor: "rgba(255,255,255,0.75)",
            border: "1px solid rgba(15, 23, 42, 0.10)",
          }}
        >
          <Typography sx={{ lineHeight: 1.9 }}>
            • Atua como profissional na área de Metrologia desde 2009
            <br />• Qualificado para aplicar Auditorias Internas da ISO 17025:2017
            <br />• Pós-graduação (especialização) em Metrologia
            <br />• Tecnólogo em Mecatrônica Industrial
            <br />• Técnico em Manutenção de Equipamentos Biomédicos
            <br />• Qualificação: Instrumentista Industrial
            <br />• Participação no sub-comitê técnico de calibração (CT-08 eletro-eletrônica)
          </Typography>
        </Paper>

        <Box
          sx={{
            p: 2.5,
            borderRadius: 3,
            bgcolor: "rgba(18, 85, 112, 0.08)",
            border: "1px solid rgba(18, 85, 112, 0.18)",
          }}
        >
          <Typography fontWeight={900} sx={{ mb: 0.5 }}>
            Posicionamento executivo
          </Typography>
          <Typography color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Liderança técnica para transformar requisitos normativos em eficiência, rastreabilidade e
            confiança metrológica — com foco em preparação para acreditação.
          </Typography>
        </Box>
      </Stack>
    </SlideShell>
  );
}

/** ---------- Slide 3 ---------- */
function Slide03() {
  return (
    <SlideShell
      badge="Escopo de trabalho"
      title="Consultoria e Treinamentos"
      subtitle="ISO 17025 e ISO 9001 aplicadas ao dia a dia do laboratório"
    >
      <Paper
        sx={{
          p: 2.5,
          borderRadius: 3,
          bgcolor: "rgba(255,255,255,0.75)",
          border: "1px solid rgba(15, 23, 42, 0.10)",
        }}
      >
        <Typography sx={{ lineHeight: 1.95 }}>
          • Consultorias e treinamentos de calibração
          <br />• Incerteza de medição na avaliação da conformidade
          <br />• Interpretação e elaboração de certificado de calibração (ABNT NBR ISO/IEC 17025:2017)
          <br />• Auditoria interna e implementação de acreditação (ISO 17025:2017)
          <br />• Implementação e integração com ISO 9001:2015
        </Typography>
      </Paper>
    </SlideShell>
  );
}

/** ---------- Slide 4 ---------- */
function Slide04() {
  return (
    <SlideShell
      badge="Metrologia"
      title="Conceitos básicos"
      subtitle="O que é metrologia? Um pouco de história • Inmetro como referência"
    >
      <Stack spacing={2}>
        <Typography color="text.secondary" sx={{ lineHeight: 1.9 }}>
          Metrologia é a base para decisões técnicas confiáveis: mede, compara e garante
          rastreabilidade. Nesta etapa, o objetivo é nivelar conceitos e conectar com a estrutura do
          sistema metrológico nacional.
        </Typography>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <Paper sx={{ p: 2.5, borderRadius: 3, flex: 1, border: "1px solid rgba(15,23,42,0.10)" }}>
            <Typography fontWeight={900} sx={{ mb: 1 }}>
              Tópicos
            </Typography>
            <Typography sx={{ lineHeight: 1.9 }}>
              • Definições e aplicações
              <br />• Evolução histórica
              <br />• Papel do Inmetro
            </Typography>
          </Paper>

          <Paper
            sx={{
              p: 2.5,
              borderRadius: 3,
              flex: 1,
              bgcolor: "rgba(18, 85, 112, 0.08)",
              border: "1px solid rgba(18, 85, 112, 0.18)",
            }}
          >
            <Typography fontWeight={900} sx={{ mb: 1 }}>
              Resultado esperado
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.9 }}>
              Clareza sobre rastreabilidade, padrões, incerteza e por que isso sustenta a ISO 17025.
            </Typography>
          </Paper>
        </Stack>
      </Stack>
    </SlideShell>
  );
}

/** ---------- Slide 5 ---------- */
function Slide05() {
  return (
    <SlideShell
      badge="Implementação do SGQ – ISO 17025"
      title="Estrutura organizacional do Inmetro"
      subtitle="Visão de alto nível do ecossistema metrológico"
    >
      <Stack spacing={2}>
        <Typography color="text.secondary" sx={{ lineHeight: 1.9 }}>
          Aqui você pode inserir um diagrama/imagem do organograma (do slide original) e explicar
          como as áreas se conectam com acreditação e rastreabilidade.
        </Typography>

        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
            border: "1px dashed rgba(15,23,42,0.25)",
            bgcolor: "rgba(255,255,255,0.65)",
            minHeight: 220,
            display: "grid",
            placeItems: "center",
          }}
        >
          <Typography fontWeight={800} color="text.secondary">
            Placeholder do diagrama (Inmetro)
          </Typography>
        </Paper>

        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
          Dica: exporte o slide como PNG e substitua esse bloco por um componente de imagem.
        </Typography>
      </Stack>
    </SlideShell>
  );
}

const SLIDES = [
  { key: "s1", title: "Seja Bem-vindo!", component: <Slide01 /> },
  { key: "s2", title: "Sobre o autor", component: <Slide02 /> },
  { key: "s3", title: "Escopo de trabalho", component: <Slide03 /> },
  { key: "s4", title: "Metrologia", component: <Slide04 /> },
  { key: "s5", title: "Inmetro (estrutura)", component: <Slide05 /> },
];

/** ---------- Main Deck Page ---------- */
export default function Iso17025SlideDeck() {
  const deckRef = React.useRef(null);
  const { isFs, toggle } = useFullscreen();

  const [index, setIndex] = React.useState(0);
  const [direction, setDirection] = React.useState(1); // 1 next, -1 prev
  const [gridMode, setGridMode] = React.useState(false);

  const total = SLIDES.length;
  const progress = ((index + 1) / total) * 100;

  const go = React.useCallback(
    (nextIndex) => {
      const clamped = clamp(nextIndex, 0, total - 1);
      setDirection(clamped > index ? 1 : -1);
      setIndex(clamped);
    },
    [index, total]
  );

  const next = React.useCallback(() => go(index + 1), [go, index]);
  const prev = React.useCallback(() => go(index - 1), [go, index]);

  React.useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "ArrowRight" || e.key === "PageDown") next();
      if (e.key === "ArrowLeft" || e.key === "PageUp") prev();
      if (e.key.toLowerCase() === "g") setGridMode((v) => !v);
      if (e.key === "Escape" && gridMode) setGridMode(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [next, prev, gridMode]);

  const slide = SLIDES[index];

  return (
    <Box ref={deckRef} sx={{ minHeight: "100vh", bgcolor: "#F6F8FB" }}>
      <AppBar position="sticky" color="default" sx={{ boxShadow: "none" }}>
        <Toolbar sx={{ borderBottom: "1px solid rgba(15, 23, 42, 0.08)" }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: BRAND,
              color: "white",
              display: "grid",
              placeItems: "center",
              fontWeight: 950,
              mr: 1.5,
            }}
          >
            E
          </Box>

          <Box sx={{ flexGrow: 1 }}>
            <Typography fontWeight={950} lineHeight={1.1}>
              Implementação do SGQ – ISO 17025
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Slide {index + 1} de {total} • {slide.title}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Grid (tecla G)">
              <IconButton onClick={() => setGridMode((v) => !v)}>
                <GridViewIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={isFs ? "Sair do fullscreen" : "Fullscreen"}>
              <IconButton onClick={() => toggle(deckRef.current)}>
                {isFs ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>

            <Button
              variant="contained"
              onClick={() => window.open("https://wa.me/5511971301709", "_blank")}
            >
              Contato
            </Button>
          </Stack>
        </Toolbar>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 3,
            "& .MuiLinearProgress-bar": { backgroundColor: BRAND },
          }}
        />
      </AppBar>

      <Container sx={{ py: { xs: 2.5, md: 4 } }}>
        {/* GRID MODE */}
        {gridMode ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
              gap: 2,
            }}
          >
            {SLIDES.map((s, i) => (
              <Paper
                key={s.key}
                onClick={() => {
                  setGridMode(false);
                  go(i);
                }}
                sx={{
                  p: 2.5,
                  borderRadius: 4,
                  cursor: "pointer",
                  border: i === index ? `2px solid ${BRAND}` : "1px solid rgba(15,23,42,0.10)",
                  transition: "transform .15s ease",
                  "&:hover": { transform: "translateY(-2px)" },
                }}
              >
                <Typography fontWeight={950} sx={{ mb: 0.5 }}>
                  {i + 1}. {s.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Clique para abrir
                </Typography>
              </Paper>
            ))}
          </Box>
        ) : (
          <>
            {/* SLIDE STAGE */}
            <Box sx={{ position: "relative" }}>
              <AnimatePresence mode="wait">
                <MotionBox
                  key={slide.key}
                  initial={{ opacity: 0, x: direction > 0 ? 60 : -60 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: direction > 0 ? -60 : 60 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}
                >
                  {slide.component}
                </MotionBox>
              </AnimatePresence>

              {/* Controls */}
              <Stack
                direction="row"
                spacing={1.2}
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 2 }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton onClick={prev} disabled={index === 0}>
                    <ArrowBackIcon />
                  </IconButton>
                  <IconButton onClick={next} disabled={index === total - 1}>
                    <ArrowForwardIcon />
                  </IconButton>

                  <Typography variant="body2" color="text.secondary">
                    Use ← → / PageUp PageDown • G para grid
                  </Typography>
                </Stack>

                {/* Dots */}
                <Stack direction="row" spacing={0.8} alignItems="center">
                  {SLIDES.map((s, i) => (
                    <Box
                      key={s.key}
                      onClick={() => go(i)}
                      sx={{
                        width: i === index ? 18 : 10,
                        height: 10,
                        borderRadius: 999,
                        cursor: "pointer",
                        bgcolor: i === index ? BRAND : "rgba(15,23,42,0.18)",
                        transition: "all .15s ease",
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
}
